import { firestore, functions } from "../firebase";
import DB_PATH from "../DB_PATH";
import {
  AppCard,
  AppStageDef,
  DEFAULT_APP_STAGES,
} from "../../types/AppPipeline";

// Batch portfolio health for the board's card faces (getAppPortfolioSnapshot
// Cloud Function). One call for all visible communities; never per-card. Shape
// mirrors AppPortfolioSnapshot in functions/src/portfolio.ts verbatim.
export type AppPortfolioSnapshot = {
  communityId: string;
  ok: boolean;
  launch: {
    ready: number;
    total: number;
    percent: number;
    complete: boolean;
    topBlockers: {
      item: string;
      status: "missing" | "partial";
      detail: string;
    }[];
  } | null;
  integrations: {
    stripeConnected: boolean;
    stripeWebhook: boolean;
    stripeHasPrices: boolean;
    revenueCatConfigured: boolean;
    appleCreds: "valid" | "partial" | "missing";
    googleCreds: "valid" | "partial" | "missing";
    zoomConnected: boolean;
    zoomStale: boolean;
  } | null;
  members: { paying: number | null } | null;
  activity: { dau: number; wau: number; mau: number } | null;
  build: {
    status: string;
    conclusion: string | null;
    startedAt: number | null;
    url: string | null;
  } | null;
  store: { ios: boolean; android: boolean };
};

export const getAppPortfolioSnapshot = async (
  communityIds: string[]
): Promise<Record<string, AppPortfolioSnapshot>> => {
  const callable = functions.httpsCallable("getAppPortfolioSnapshot");
  const res = await callable({ communityIds });
  return ((res.data as any)?.snapshots || {}) as Record<
    string,
    AppPortfolioSnapshot
  >;
};

// Client access to the app portfolio pipeline at AppPipeline/global. The whole
// surface is operator-only in the UI (isBoardOperator); like the issue board,
// server-side hardening would need the firestore.rules rework, so the gate is
// client-side + MCP-side for now.

const PIPELINE_DOC_ID = "global";

const pipelineRef = () =>
  firestore.collection(DB_PATH.APP_PIPELINE).doc(PIPELINE_DOC_ID);

const cardsCollection = () =>
  pipelineRef().collection(DB_PATH.APP_PIPELINE_CARDS);

const nowIso = () => new Date().toISOString();

const sanitizeStage = (raw: any): AppStageDef | null => {
  if (!raw || typeof raw.key !== "string" || !raw.key) return null;
  return {
    key: raw.key,
    label: typeof raw.label === "string" && raw.label ? raw.label : raw.key,
    ...(raw.tone ? { tone: raw.tone } : {}),
  };
};

const sanitizeCard = (id: string, data: any): AppCard => ({
  id,
  communityId: typeof data?.communityId === "string" ? data.communityId : id,
  name: typeof data?.name === "string" ? data.name : id,
  stage: typeof data?.stage === "string" ? data.stage : DEFAULT_APP_STAGES[0].key,
  position: typeof data?.position === "number" ? data.position : 0,
  nextAction: typeof data?.nextAction === "string" ? data.nextAction : "",
  notes: typeof data?.notes === "string" ? data.notes : "",
  owner: typeof data?.owner === "string" ? data.owner : "",
  ...(typeof data?.outreachLeadId === "string" && data.outreachLeadId
    ? { outreachLeadId: data.outreachLeadId }
    : {}),
  stageChangedAt:
    typeof data?.stageChangedAt === "string" ? data.stageChangedAt : "",
  createdAt: typeof data?.createdAt === "string" ? data.createdAt : "",
  updatedAt: typeof data?.updatedAt === "string" ? data.updatedAt : "",
});

// ─── stages ──────────────────────────────────────────────────────────────────

export const listenToAppStages = (
  onChange: (stages: AppStageDef[]) => void,
  onError?: (err: Error) => void
): (() => void) =>
  pipelineRef().onSnapshot(
    (snap) => {
      const raw = snap.exists ? (snap.data() as any)?.stages : null;
      const stages = Array.isArray(raw)
        ? (raw.map(sanitizeStage).filter(Boolean) as AppStageDef[])
        : [];
      onChange(stages.length ? stages : DEFAULT_APP_STAGES);
    },
    (err) => {
      console.error("Error listening to app pipeline stages:", err);
      onError?.(err);
    }
  );

export const setAppStages = async (stages: AppStageDef[]): Promise<void> => {
  const keys = stages.map((s) => s.key);
  const dupe = keys.find((k, i) => keys.indexOf(k) !== i);
  if (dupe) throw new Error(`Duplicate stage key "${dupe}".`);
  if (!stages.length) throw new Error("Keep at least one stage.");
  await pipelineRef().set({ stages, updatedAt: nowIso() }, { merge: true });
};

// ─── cards ───────────────────────────────────────────────────────────────────

export const listenToAppCards = (
  onChange: (cards: AppCard[]) => void,
  onError?: (err: Error) => void
): (() => void) =>
  cardsCollection()
    .orderBy("position", "asc")
    .onSnapshot(
      (snap) => onChange(snap.docs.map((d) => sanitizeCard(d.id, d.data()))),
      (err) => {
        console.error("Error listening to app pipeline cards:", err);
        onError?.(err);
      }
    );

// Seed one card per community that isn't on the board yet. The doc id is the
// communityId, so re-running can never create duplicates.
export const seedAppCards = async (
  apps: { id: string; name: string }[]
): Promise<number> => {
  const fresh = apps.filter((a) => a.id);
  if (!fresh.length) return 0;
  const batch = firestore.batch();
  const now = nowIso();
  fresh.forEach((app, i) => {
    batch.set(
      cardsCollection().doc(app.id),
      {
        communityId: app.id,
        name: app.name || app.id,
        stage: DEFAULT_APP_STAGES[0].key,
        position: Date.now() + i,
        nextAction: "",
        notes: "",
        owner: "",
        stageChangedAt: now,
        createdAt: now,
        updatedAt: now,
      },
      { merge: false }
    );
  });
  await batch.commit();
  return fresh.length;
};

export const updateAppCard = async (
  id: string,
  patch: Partial<
    Pick<
      AppCard,
      "stage" | "position" | "nextAction" | "notes" | "owner" | "name"
    >
  >
): Promise<void> => {
  const update: Record<string, any> = { updatedAt: nowIso() };
  if (typeof patch.stage === "string") {
    update.stage = patch.stage;
    update.stageChangedAt = nowIso();
  }
  if (typeof patch.position === "number") update.position = patch.position;
  if (typeof patch.nextAction === "string")
    update.nextAction = patch.nextAction.trim();
  if (typeof patch.notes === "string") update.notes = patch.notes.trim();
  if (typeof patch.owner === "string") update.owner = patch.owner.trim();
  if (typeof patch.name === "string" && patch.name.trim())
    update.name = patch.name.trim();
  await cardsCollection().doc(id).set(update, { merge: true });
};

export const moveAppCard = (
  id: string,
  stage: string,
  position: number
): Promise<void> => updateAppCard(id, { stage, position });

export const deleteAppCard = async (id: string): Promise<void> => {
  await cardsCollection().doc(id).delete();
};
